// this file come from @local/mobile

import queryString from 'query-string'

import { ValueOf, Nullish } from '@web/types'

import links from '../constants/links'
import restTemplate from '../restTemplate'
import { extractErrorMessage, neloLogger } from '../utils'
import { THUMBNAIL_SERVICES, THUMBNAIL_TYPES } from './photoInfra.constant'

export const hasThumbnailType = (url: string) => /type(=|%3D)/.test(url)

export const convertThumbUrlToHttps = (url: string) => {
  if (/^http:\/\/beta\.ldb/g.test(url)) {
    return url.replace(/http:\/\/beta\.ldb\.phinf\.naver\.net/g, 'https://beta.ssl.phinf.net/ldb')
  }

  if (/^http:\/\/ldb/g.test(url)) {
    return url.replace(/http:\/\/ldb\.phinf\.naver\.net/g, 'https://ldb-phinf.pstatic.net')
  }

  return url
}

const fromLDB = (url: string) =>
  /^http:\/\/beta\.ldb/g.test(url) || /^http:\/\/ldb\.phinf/g.test(url) || /^https:\/\/ldb-phinf/g.test(url)

const fromMyPlace = (url: string) =>
  /^https:\/\/beta-myplace\.ssl\.phinf/g.test(url) || /^https:\/\/myplace-phinf\.pstatic\.net/g.test(url)

const fromBooking = (url: string) =>
  /^http:\/\/beta.naverbooking.phinf.naver.net/g.test(url) ||
  /^https:\/\/beta.ssl.phinf.net\/naverbooking/g.test(url) ||
  /^http:\/\/naverbooking.phinf\.naver\.net/g.test(url) ||
  /^https:\/\/naverbooking-phinf\.pstatic\.net/g.test(url)

export const fromContact = (url: string) => /^https:\/\/phinf\.pstatic\.net\/contact/g.test(url)

const fromCommon = (url: string) => /^https:\/\/search\.pstatic\.net\/common/g.test(url)

const fromBlog = (url: string) =>
  /^http:\/\/blogfiles.naver.net/g.test(url) ||
  /^https:\/\/blogfiles.naver.net/g.test(url) ||
  /^http:\/\/blogpfthumb.phinf.naver.net/g.test(url) ||
  /^https:\/\/blogpfthumb.phinf.naver.net/g.test(url)

const fromPup = (url: string) =>
  /^https:\/\/beta-pup-review\.ssl\.phinf\.net/g.test(url) || /^https:\/\/pup-review-phinf\.pstatic\.net/g.test(url)

const fromMyMoney = (url: string) =>
  /^https:\/\/beta-mymoney\.ssl\.phinf\.net/g.test(url) || /^https:\/\/mymoney-phinf\.pstatic\.net/g.test(url)

const fromVideoPlatform = (url: string) =>
  /^https:\/\/beta\.ssl\.phinf\.net\/video/g.test(url) || /^https:\/\/video-phinf\.pstatic\.net/g.test(url)

const isGIF = (url: string) => /(https?:\/\/.*\.(?:gif))/i.test(url)

const getThumbnailService = (url: string): ValueOf<typeof THUMBNAIL_SERVICES> | null => {
  if (fromPup(url)) {
    return THUMBNAIL_SERVICES.pup
  }

  if (fromMyPlace(url)) {
    return THUMBNAIL_SERVICES.myplace
  }

  if (fromBooking(url)) {
    return THUMBNAIL_SERVICES.booking
  }

  if (fromContact(url)) {
    return THUMBNAIL_SERVICES.contact
  }

  if (fromLDB(url)) {
    return THUMBNAIL_SERVICES.ldb
  }

  if (fromCommon(url)) {
    return THUMBNAIL_SERVICES.common
  }

  if (fromBlog(url)) {
    return THUMBNAIL_SERVICES.blog
  }

  if (fromMyMoney(url)) {
    return THUMBNAIL_SERVICES.mymoney
  }

  if (fromVideoPlatform(url)) {
    return THUMBNAIL_SERVICES.video_platform
  }

  return null
}

const convertThumbnail = ({ url, type = 'f110_110' }: { url: Nullish<string>; type: string }): string => {
  if (!url) {
    return ''
  }

  const service = getThumbnailService(url)

  if (service === THUMBNAIL_SERVICES.blog || service === THUMBNAIL_SERVICES.ldb) {
    return links.PHOTO_INFRA_COMMON.concat(
      queryString.stringify(
        {
          autoRotate: true,
          // @ts-ignore
          type: THUMBNAIL_TYPES[service][type] ? type : THUMBNAIL_TYPES.common.w560_sharpen,
          src: url,
        },
        {
          sort: false,
        },
      ),
    )
  }

  if (!service || !Object.keys(THUMBNAIL_SERVICES).includes(service)) {
    return url
  }

  if (service === THUMBNAIL_SERVICES.myplace && isGIF(url)) {
    return convertThumbUrlToHttps(url)
  }

  // @ts-ignore
  if (!THUMBNAIL_TYPES[service][type]) {
    return url
  }

  const splitUrl = url.split('?')[1]

  if (service === THUMBNAIL_SERVICES.common && splitUrl) {
    const { src } = queryString.parse(splitUrl)
    return links.PHOTO_INFRA_COMMON.concat(
      queryString.stringify({
        autoRotate: true,
        quality: 60,
        src,
        // @ts-ignore
        type: THUMBNAIL_TYPES[service][type],
      }),
    )
  }

  // @ts-ignore
  return convertThumbUrlToHttps(url).split('?type')[0].concat(`?type=${THUMBNAIL_TYPES[service][type]}`)
}

function getImageDimensions(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    if (!file) {
      reject(new Error('파일이 제공되지 않았습니다.'))
    }

    const img = new Image()

    img.onload = () => {
      // 이미지의 원본 가로 세로 크기 얻기
      const width = img.naturalWidth
      const height = img.naturalHeight

      // 메모리 해제를 위해 객체 URL을 폐기
      URL.revokeObjectURL(img.src)
      resolve({ width, height })
    }

    img.onerror = error => {
      // 에러 발생 시 객체 URL 폐기
      URL.revokeObjectURL(img.src)
      reject(new Error(`이미지 로드에 실패했습니다. ${extractErrorMessage(error)}`))
    }

    // 이미지 파일에 대한 임시 URL 생성
    img.src = URL.createObjectURL(file)
  })
}

const uploadImage = async (file: File): Promise<{ [key: string]: string } | null> => {
  try {
    const { width, height } = await getImageDimensions(file)

    const data = new FormData()
    data.append('file', file, file.name)

    const { data: result } = await restTemplate.post({
      url: restTemplate.FILE_UPLOAD_END_POINTS.mediaItem,
      data,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    })

    return { ...result, thumbnail: result.url, _id: result.id, width, height }
  } catch (error) {
    neloLogger('error', '[photoInfra > uploadImage] 이미지 업로드 중 에러가 발생했습니다', {
      error,
      errorMessage: extractErrorMessage(error),
    })
    alert('사진 업로드에 실패했습니다')
  }

  return null
}

const photoInfra = {
  convertThumbnail,
  type: THUMBNAIL_TYPES.myplace,
  THUMBNAIL_TYPES,
  uploadImage,
}

export default photoInfra
